import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';

import { AssociatedCustomer, PaginationInput, UserType } from 'src/shared/types';
import {
    CreateProjectUserQueryResult,
    GetCustomerUsersQueryResult,
    getCustomerUsersQuery,
    createProjectUserQuery,
    CheckUsernameAvailableQueryResult,
    checkUsernameAvailableQuery,
    updateProjectUserQuery,
    enableProjectuserQuery,
    disableProjectuserQuery,
    resendInvitationEmailQuery,
    CreateUserQueryResult,
    createUserQuery,
    GetCustomerAdminUsersQueryResult,
    getCustomerAdminUsersQuery,
    getCustomerUsersForSuperAdminQuery,
    GetCustomerUsersForSuperAdminQueryResult,
    userManagementSuperAdminOverviewQuery,
    UserManagementSuperAdminOverviewResult,
    GetUserResult,
    getUserQuery,
    updateUserQuery,
    unassignUserFromOrganisationQuery,
    reassignUserFromOrganisationQuery,
    HasCustomerAdminUserQueryResult,
    hasCustomerAdminUserQuery,
    upsertUserProjectsForCustomerMutation,
    UpsertUserProjectsForCustomerResult,
    removeUserProjectsForCustomerMutation,
    RemoveUserProjectsForCustomerResult,
    userManagementMatrixRolesQuery,
    UserManagementMatrixRolesQueryResult,
    bulkApplyUserManagementSiteRoleMutation,
    BulkApplyUserManagementSiteRoleMutationResult,
} from './queries/userManagementQuery';
import { UserProjectAccess } from '../userManagementSlice';
import type { UpsertUserProjectAccessInput } from '../../userProjectAccessMutations';

type CreateProjectUserParams = {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    projects: UserProjectAccess[];
    customerId: string;
};

type UpdateProjectUserParams = Omit<CreateProjectUserParams, 'email'> & { tnc_consent: boolean; cognitoId: string; notify: boolean };

type CheckUsernameAvailableQueryParams = {
    email: string;
    customerId: string;
};

type GetUserQueryParams = {
    email: string;
};

type UpdateUserStatus = {
    email: string;
    notify: boolean;
};

type CreateUserParams = {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    projects: UserProjectAccess[];
    customers: Partial<AssociatedCustomer>[];
    tnc_consent: boolean;
    isNatureMetricsUser: boolean;
};

type UpdateUserParams = Omit<CreateUserParams, 'email' | 'isNatureMetricsUser'> & { cognitoId: string };

type GetCustomerAdminUsersQueryParams = {
    customerId: string;
};

type GetCustomerUsersForSuperAdminQueryParams = {
    filters: {
        customerId: string;
        emailQuery?: string;
        userType?: UserType | null;
    };
    pagination: PaginationInput;
};

type GetCustomerUsersQueryParams = {
    filters: {
        customerId: string;
        emailQuery?: string;
    };
    pagination: {
        page: number;
        pageSize: number;
    };
};

type UserManagementSuperAdminOverviewParams = {
    customerId: string;
    pagination: PaginationInput;
    userType?: UserType | null;
};

type AssignUserToOrganisationParams = {
    id: number;
    customerId: string;
    notify: boolean;
};

type UpsertUserProjectsForCustomerParams = {
    customerId: string;
    userId: number;
    projects: UpsertUserProjectAccessInput[];
};

type RemoveUserProjectsForCustomerParams = {
    customerId: string;
    userId: number;
    projectIds: string[];
};

type UserManagementMatrixRolesParams = {
    customerId: string;
    searchText?: string;
    includeRolesFromUserId?: number;
};

type BulkApplyUserManagementSiteRoleParams = {
    customerId: string;
    userId: number;
    role: string;
    assign: boolean;
    searchText?: string;
};

/** Matrix `userCountSummary` must refetch after any change to who holds which site roles */
export const USER_COUNT_SUMMARY_TAG = { type: 'UserCountSummary' as const, id: 'LIST' };

const userManagementGraphSlice = graphApiSlice.injectEndpoints({
    endpoints: builder => ({
        createUser: builder.mutation<CreateUserQueryResult, CreateUserParams>({
            query: params => ({
                body: createUserQuery,
                variables: { input: params },
            }),
            invalidatesTags: response => ['users', USER_COUNT_SUMMARY_TAG, { type: 'user', id: response?.id }],
        }),
        adminUpdateUser: builder.mutation<boolean, UpdateUserParams>({
            query: params => {
                const { cognitoId, ...inputParams } = params;
                return {
                    body: updateUserQuery,
                    variables: { input: inputParams, cognitoId: cognitoId },
                };
            },
        }),

        createProjectUser: builder.mutation<CreateProjectUserQueryResult, CreateProjectUserParams>({
            query: params => ({
                body: createProjectUserQuery,
                variables: params,
            }),
            invalidatesTags: response => ['users', USER_COUNT_SUMMARY_TAG, { type: 'user', id: response?.id }],
        }),

        getCustomerUsers: builder.query<GetCustomerUsersQueryResult['users'], GetCustomerUsersQueryParams>({
            query: params => ({
                body: getCustomerUsersQuery,
                variables: params,
            }),
            transformResponse: (response: GetCustomerUsersQueryResult) => {
                return response?.users;
            },
            providesTags: ['users'],
        }),
        checkUsernameAvailable: builder.query<CheckUsernameAvailableQueryResult, CheckUsernameAvailableQueryParams>({
            query: params => ({
                body: checkUsernameAvailableQuery,
                variables: params,
            }),
        }),
        updateProjectUser: builder.mutation<boolean, UpdateProjectUserParams>({
            query: params => ({
                body: updateProjectUserQuery,
                variables: params,
            }),
            invalidatesTags: ['users', USER_COUNT_SUMMARY_TAG],
        }),
        upsertUserProjectsForCustomer: builder.mutation<boolean, UpsertUserProjectsForCustomerParams>({
            query: params => ({
                body: upsertUserProjectsForCustomerMutation,
                variables: params,
            }),
            transformResponse: (response: UpsertUserProjectsForCustomerResult) => response.users.upsertProjects,
            invalidatesTags: ['users', USER_COUNT_SUMMARY_TAG],
        }),
        removeUserProjectsForCustomer: builder.mutation<boolean, RemoveUserProjectsForCustomerParams>({
            query: params => ({
                body: removeUserProjectsForCustomerMutation,
                variables: params,
            }),
            transformResponse: (response: RemoveUserProjectsForCustomerResult) => response.users.removeProjects,
            invalidatesTags: ['users', USER_COUNT_SUMMARY_TAG],
        }),
        enableUser: builder.mutation<boolean, UpdateUserStatus>({
            query: params => ({
                body: enableProjectuserQuery,
                variables: params,
            }),
            invalidatesTags: ['users', USER_COUNT_SUMMARY_TAG],
        }),
        disableUser: builder.mutation<boolean, UpdateUserStatus>({
            query: params => ({
                body: disableProjectuserQuery,
                variables: params,
            }),
            invalidatesTags: ['users', USER_COUNT_SUMMARY_TAG],
        }),
        resendInvitationEmail: builder.mutation<boolean, { email: string; password: string }>({
            query: params => ({
                body: resendInvitationEmailQuery,
                variables: params,
            }),
            invalidatesTags: ['users', USER_COUNT_SUMMARY_TAG],
        }),
        getUserManagementMatrixRoles: builder.query<string[], UserManagementMatrixRolesParams>({
            query: params => ({
                body: userManagementMatrixRolesQuery,
                variables: {
                    input: {
                        customerId: params.customerId,
                        searchText: params.searchText,
                        includeRolesFromUserId: params.includeRolesFromUserId,
                    },
                },
            }),
            transformResponse: (response: UserManagementMatrixRolesQueryResult) => response.usersQueries.userManagementMatrixRoles,
        }),
        bulkApplyUserManagementSiteRole: builder.mutation<
            BulkApplyUserManagementSiteRoleMutationResult['users']['bulkApplyUserManagementSiteRole'],
            BulkApplyUserManagementSiteRoleParams
        >({
            query: params => ({
                body: bulkApplyUserManagementSiteRoleMutation,
                variables: {
                    input: {
                        customerId: params.customerId,
                        userId: params.userId,
                        role: params.role,
                        assign: params.assign,
                        searchText: params.searchText,
                    },
                },
            }),
            transformResponse: (response: BulkApplyUserManagementSiteRoleMutationResult) => response.users.bulkApplyUserManagementSiteRole,
            invalidatesTags: ['users', USER_COUNT_SUMMARY_TAG],
        }),
        getCustomerAdminUsers: builder.query<GetCustomerAdminUsersQueryResult, GetCustomerAdminUsersQueryParams>({
            query: params => ({
                body: getCustomerAdminUsersQuery,
                variables: params,
            }),
            providesTags: ['users'],
        }),

        getUserManagementSuperAdminOverview: builder.query<
            {
                customerAdmins: UserManagementSuperAdminOverviewResult['customerAdmins'];
                users: UserManagementSuperAdminOverviewResult['admin']['users']['users'];
                pagination: UserManagementSuperAdminOverviewResult['admin']['users']['pagination'];
            },
            UserManagementSuperAdminOverviewParams
        >({
            query: params => ({
                body: userManagementSuperAdminOverviewQuery,
                variables: {
                    customerId: params.customerId,
                    pagination: params.pagination,
                    userType: params.userType ?? null,
                },
            }),
            transformResponse: (response: UserManagementSuperAdminOverviewResult) => ({
                customerAdmins: response.customerAdmins,
                users: response.admin.users.users,
                pagination: response.admin.users.pagination,
            }),
            providesTags: ['users'],
        }),

        getCustomerUsersForSuperAdmin: builder.query<
            GetCustomerUsersForSuperAdminQueryResult['admin']['users'],
            GetCustomerUsersForSuperAdminQueryParams
        >({
            query: params => ({
                body: getCustomerUsersForSuperAdminQuery,
                variables: params,
            }),
            transformResponse: (response: GetCustomerUsersForSuperAdminQueryResult) => {
                return response?.admin.users;
            },
            providesTags: ['users'],
        }),

        getUser: builder.query<GetUserResult, GetUserQueryParams>({
            query: params => ({
                body: getUserQuery,
                variables: params,
            }),
            providesTags: response => [{ type: 'user', id: response?.admin.user.id }],
        }),

        unassignUserFromOrganisation: builder.mutation<boolean, AssignUserToOrganisationParams>({
            query: params => ({
                body: unassignUserFromOrganisationQuery,
                variables: params,
            }),
            invalidatesTags: ['users', USER_COUNT_SUMMARY_TAG],
        }),

        reassignUserToOrganisation: builder.mutation<boolean, AssignUserToOrganisationParams>({
            query: params => ({
                body: reassignUserFromOrganisationQuery,
                variables: params,
            }),
            invalidatesTags: ['users', USER_COUNT_SUMMARY_TAG],
        }),

        hasCustomerAdminUser: builder.query<HasCustomerAdminUserQueryResult, GetCustomerAdminUsersQueryParams>({
            query: params => ({
                body: hasCustomerAdminUserQuery,
                variables: params,
            }),
        }),
    }),
});

export const {
    useCreateUserMutation,
    useAdminUpdateUserMutation,
    useCreateProjectUserMutation,
    useGetCustomerUsersQuery,
    useCheckUsernameAvailableQuery,
    useUpdateProjectUserMutation,
    useUpsertUserProjectsForCustomerMutation,
    useRemoveUserProjectsForCustomerMutation,
    useEnableUserMutation,
    useDisableUserMutation,
    useResendInvitationEmailMutation,
    useGetUserManagementMatrixRolesQuery,
    useBulkApplyUserManagementSiteRoleMutation,
    useGetCustomerAdminUsersQuery,
    useLazyGetCustomerAdminUsersQuery,
    useGetUserManagementSuperAdminOverviewQuery,
    useGetCustomerUsersForSuperAdminQuery,
    useGetUserQuery,
    useUnassignUserFromOrganisationMutation,
    useReassignUserToOrganisationMutation,
    useHasCustomerAdminUserQuery,
} = userManagementGraphSlice;
